import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Upload, FileText, LayoutGrid, User } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/Loading.json';
import uploadAnimation from '../assets/Upload Files.json';
import { PERMISSIONS, usePermissionHook, PERMISSION_DENIED_MESSAGE, permissionGuardClass } from '../utils/permissions';
import PagePermissionWrapper from '../components/PagePermissionWrapper';
import CampaignTypeSwitcher from '../components/CampaignTypeSwitcher';
import { ModalReporter } from '../context/ModalContext';
import { LimelightNav } from '../components/ui/limelight-nav';
import { ShineBorder } from '../components/ui/shine-border';
import {
  fetchGeneralTemplates,
  fetchPersonalTemplates,
  createGeneralTemplate,
  createPersonalTemplate,
  updateGeneralTemplate,
  updatePersonalTemplate,
  deleteGeneralTemplate,
  deletePersonalTemplate,
} from '../redux/slices/campaignTemplateSlice';

const emptyForm = {
  templateName: '',
  campaignImage: '',
  campaignImage2: '',
  campaignImage3: '',
  campaignTitle: '',
  campaignContent: '',
  campaignContentShort: '',
  campaignFeatures: '',
  campaignTermsAndConditions: '',
};

const emptyFormPersonal = {
  templateName: '',
  campaignImage: '',
  campaignImage2: '',
  campaignTitle: '',
  campaignContent: '',
  campaignContentShort: '',
  campaignFeatures: '',
  campaignTermsAndConditions: '',
};

// API campaignFeatures: # ile ayrılmış. Form: newline ile.
const featuresToForm = (s) => (s || '').split('#').join('\n');
const formToFeatures = (s) => (s || '').split('\n').filter(Boolean).join('#');

const CampaignTemplates = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'personal'
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(activeTab === 'general' ? { ...emptyForm } : { ...emptyFormPersonal });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [emptyFields, setEmptyFields] = useState([]);

  const {
    generalTemplates,
    personalTemplates,
    generalLoading,
    personalLoading,
    generalError,
    personalError,
    actionLoading,
  } = useSelector((state) => state.campaignTemplates);

  const isGeneral = activeTab === 'general';
  const canManageCampaigns = usePermissionHook(PERMISSIONS.CAMPAIGNS_MANAGE);
  const templates = isGeneral ? generalTemplates : personalTemplates;
  const loading = isGeneral ? generalLoading : personalLoading;
  const error = isGeneral ? generalError : personalError;

  useEffect(() => {
    if (activeTab === 'general') {
      dispatch(fetchGeneralTemplates());
    } else {
      dispatch(fetchPersonalTemplates());
    }
    setModalOpen(false);
    setDeleteConfirm(null);
    setFormData(activeTab === 'general' ? { ...emptyForm } : { ...emptyFormPersonal });
    setEditingTemplate(null);
  }, [activeTab, dispatch]);

  const resetForm = () => {
    setFormData(isGeneral ? { ...emptyForm } : { ...emptyFormPersonal });
    setEditingTemplate(null);
    setEmptyFields([]);
  };

  const isFormValid = () => {
    const required = [
      'templateName', 'campaignTitle', 'campaignContent',
      'campaignFeatures', 'campaignTermsAndConditions', 'campaignImage' // Sadece Mobil App görseli zorunlu
    ];
    return required.every((key) => {
      const v = formData[key];
      return v != null && String(v).trim() !== '';
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (emptyFields.includes(name)) setEmptyFields((prev) => prev.filter((f) => f !== name));
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (template) => {
    const base = {
      templateName: template.templateName || '',
      campaignTitle: template.campaignTitle || '',
      campaignContent: template.campaignContent || '',
      campaignContentShort: template.campaignContentShort || '',
      campaignFeatures: featuresToForm(template.campaignFeatures || ''),
      campaignTermsAndConditions: template.campaignTermsAndConditions || '',
      campaignImage: template.campaignImage || '',
      campaignImage2: template.campaignImage2 || '',
    };
    setFormData(
      isGeneral ? { ...base, campaignImage3: template.campaignImage3 || '' } : { ...base }
    );
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const handleImageUpload = (file, field) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, [field]: reader.result }));
      setEmptyFields((prev) => prev.filter((f) => f !== field));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (field) => {
    setFormData((prev) => ({ ...prev, [field]: '' }));
    setEmptyFields((prev) => (prev.includes(field) ? prev : [...prev, field]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      const missing = [];
      if (!formData.templateName?.trim()) missing.push('templateName');
      if (!formData.campaignTitle?.trim()) missing.push('campaignTitle');
      if (!formData.campaignContent?.trim()) missing.push('campaignContent');
      if (!formData.campaignFeatures?.trim()) missing.push('campaignFeatures');
      if (!formData.campaignTermsAndConditions?.trim()) missing.push('campaignTermsAndConditions');
      if (!formData.campaignImage?.trim()) missing.push('campaignImage');
      setEmptyFields(missing);
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }
    setEmptyFields([]);
    const payload = {
      ...formData,
      campaignFeatures: formToFeatures(formData.campaignFeatures),
    };

    try {
      if (editingTemplate) {
        if (isGeneral) {
          await dispatch(updateGeneralTemplate({ id: editingTemplate.id, ...payload })).unwrap();
        } else {
          await dispatch(updatePersonalTemplate({ id: editingTemplate.id, ...payload })).unwrap();
        }
        toast.success('Taslak güncellendi');
      } else {
        if (isGeneral) {
          await dispatch(createGeneralTemplate(payload)).unwrap();
        } else {
          await dispatch(createPersonalTemplate(payload)).unwrap();
        }
        toast.success('Taslak oluşturuldu');
      }
      setModalOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err || 'İşlem başarısız');
    }
  };

  const hasChanges = () => {
    if (!editingTemplate) return true;
    const orig = editingTemplate;
    const featuresMatch = formToFeatures(formData.campaignFeatures) === (orig.campaignFeatures || '');
    const baseMatch =
      (formData.templateName || '') === (orig.templateName || '') &&
      (formData.campaignTitle || '') === (orig.campaignTitle || '') &&
      (formData.campaignContent || '') === (orig.campaignContent || '') &&
      (formData.campaignContentShort || '') === (orig.campaignContentShort || '') &&
      (formData.campaignTermsAndConditions || '') === (orig.campaignTermsAndConditions || '') &&
      (formData.campaignImage || '') === (orig.campaignImage || '') &&
      (formData.campaignImage2 || '') === (orig.campaignImage2 || '');
    const img3Match = !isGeneral || (formData.campaignImage3 || '') === (orig.campaignImage3 || '');
    return !(baseMatch && featuresMatch && img3Match);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (isGeneral) {
        await dispatch(deleteGeneralTemplate(deleteConfirm.id)).unwrap();
      } else {
        await dispatch(deletePersonalTemplate(deleteConfirm.id)).unwrap();
      }
      toast.success('Taslak silindi');
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err || 'Silme başarısız');
    }
  };

  return (
    <>
      <ModalReporter open={modalOpen || !!deleteConfirm} />
      <PagePermissionWrapper requiredPermissions={[PERMISSIONS.CAMPAIGNS_READ]} pageName="Taslak Kampanyalar">
        <div className="space-y-3">
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Taslak Kampanyalar</h1>
              <p className="text-gray-500 mt-2 text-lg">
                Kampanya şablonlarınızı oluşturun. Yeni kampanya oluştururken bu taslakları kullanabilirsiniz.
              </p>
            </div>
            <CampaignTypeSwitcher />
          </div>

          {/* Tabs */}
          <LimelightNav
            items={[
              { id: 'general', icon: <LayoutGrid />, label: 'Genel Kampanyalar' },
              { id: 'personal', icon: <User />, label: 'Bana Özel' },
            ]}
            showLabels
            activeIndex={activeTab === 'general' ? 0 : 1}
            onTabChange={(i) => setActiveTab(i === 0 ? 'general' : 'personal')}
            className="bg-white border-gray-100"
          />

          {/* Header + Add */}
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex flex-wrap items-center justify-between gap-3">
            <p className="text-gray-600">
              {isGeneral ? 'Genel kampanya taslakları' : 'Bana özel kampanya taslakları'}
            </p>
            <button
              onClick={() => {
                if (!canManageCampaigns) {
                  toast.error(PERMISSION_DENIED_MESSAGE);
                  return;
                }
                openCreateModal();
              }}
              className={`flex items-center gap-2 px-6 py-2.5 bg-blue-500 text-white rounded-xl transition-colors font-medium ${permissionGuardClass(canManageCampaigns)} ${canManageCampaigns ? 'hover:bg-blue-600' : ''}`}
              aria-disabled={!canManageCampaigns}
            >
              <Plus className="w-5 h-5" /> Yeni Taslak
            </button>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex justify-center p-20">
                <Lottie animationData={loadingAnimation} loop className="w-32 h-32" />
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => (isGeneral ? dispatch(fetchGeneralTemplates()) : dispatch(fetchPersonalTemplates()))}
                  className="px-6 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600"
                >
                  Tekrar Dene
                </button>
              </div>
            ) : templates.length === 0 ? (
              <div className="p-16 text-center">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg mb-4">Henüz taslak yok</p>
                <button
                  onClick={() => {
                    if (!canManageCampaigns) {
                      toast.error(PERMISSION_DENIED_MESSAGE);
                      return;
                    }
                    openCreateModal();
                  }}
                  className={`inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl ${permissionGuardClass(canManageCampaigns)} ${canManageCampaigns ? 'hover:bg-blue-600' : ''}`}
                  aria-disabled={!canManageCampaigns}
                >
                  <Plus className="w-5 h-5" /> İlk Taslağı Oluştur
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 items-stretch">
                {templates.map((t) => (
                  <ShineBorder
                    key={t.id}
                    borderWidth={2}
                    duration={4}
                    gradient="from-blue-500 via-red-500 to-teal-400"
                    className="w-full h-full"
                  >
                    <div className="h-full flex flex-col border border-gray-100 rounded-xl p-4 hover:border-blue-200 hover:shadow-md transition-all bg-gray-50/50">
                      {t.campaignImage && (
                        <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-white">
                          <img src={t.campaignImage} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <h3 className="font-bold text-gray-900 mb-1 truncate">
                        {t.campaignTitle || t.templateName || 'İsimsiz Taslak'}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4 truncate">{t.templateName || '-'}</p>
                      <div className="flex gap-2 mt-auto">
                        <button
                          onClick={() => {
                            if (!canManageCampaigns) {
                              toast.error(PERMISSION_DENIED_MESSAGE);
                              return;
                            }
                            openEditModal(t);
                          }}
                          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 text-blue-600 rounded-xl font-medium text-sm ${permissionGuardClass(canManageCampaigns)} ${canManageCampaigns ? 'hover:bg-blue-100' : ''}`}
                          aria-disabled={!canManageCampaigns}
                        >
                          <Pencil size={16} /> Düzenle
                        </button>
                        <button
                          onClick={() => {
                            if (!canManageCampaigns) {
                              toast.error(PERMISSION_DENIED_MESSAGE);
                              return;
                            }
                            setDeleteConfirm(t);
                          }}
                          className={`flex items-center justify-center gap-2 py-2 px-4 bg-red-50 text-red-600 rounded-xl ${permissionGuardClass(canManageCampaigns)} ${canManageCampaigns ? 'hover:bg-red-100' : ''}`}
                          aria-disabled={!canManageCampaigns}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </ShineBorder>
                ))}
              </div>
            )}
          </div>
        </div>
      </PagePermissionWrapper>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 overflow-y-auto">
          <div className="bg-white rounded-xl p-4 w-full max-w-6xl max-h-[88vh] overflow-y-auto my-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-gray-900">
                {editingTemplate ? 'Taslak Düzenle' : 'Yeni Taslak Oluştur'}
              </h2>
              <button onClick={() => { setModalOpen(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Taslak Adı <span className="text-red-500">*</span></label>
                <input
                  name="templateName"
                  type="text"
                  value={formData.templateName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                    emptyFields.includes('templateName') ? 'border-red-500 animate-pulse focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'
                  }`}
                  placeholder="Örn: Yıl sonu indirimi"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kampanya Başlığı <span className="text-red-500">*</span></label>
                <input
                  name="campaignTitle"
                  type="text"
                  value={formData.campaignTitle}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                    emptyFields.includes('campaignTitle') ? 'border-red-500 animate-pulse focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'
                  }`}
                  placeholder="Kampanya başlığı"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kısa İçerik</label>
                <textarea
                  name="campaignContentShort"
                  rows={2}
                  maxLength={26}
                  value={formData.campaignContentShort}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                    emptyFields.includes('campaignContentShort') ? 'border-red-500 animate-pulse focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'
                  }`}
                  placeholder="Kısa açıklama"
                />
                <p className={`text-xs mt-1 text-right ${(formData.campaignContentShort?.length || 0) >= 26 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {formData.campaignContentShort?.length || 0}/26
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kampanya İçeriği <span className="text-red-500">*</span></label>
                <textarea
                  name="campaignContent"
                  rows={4}
                  value={formData.campaignContent}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                    emptyFields.includes('campaignContent') ? 'border-red-500 animate-pulse focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'
                  }`}
                  placeholder="Detaylı içerik"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Özellikler (her satıra bir madde) <span className="text-red-500">*</span></label>
                <textarea
                  name="campaignFeatures"
                  rows={4}
                  value={formData.campaignFeatures}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                    emptyFields.includes('campaignFeatures') ? 'border-red-500 animate-pulse focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'
                  }`}
                  placeholder="Her satıra bir özellik yazın"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şart ve Koşullar <span className="text-red-500">*</span></label>
                <textarea
                  name="campaignTermsAndConditions"
                  rows={4}
                  value={formData.campaignTermsAndConditions}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                    emptyFields.includes('campaignTermsAndConditions') ? 'border-red-500 animate-pulse focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'
                  }`}
                  placeholder="Kampanya şartları"
                />
              </div>

              {/* Mobil Görsel - EditCampaignModal ile aynı yapı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobil App Görseli <span className="text-red-500">*</span></label>
                <div
                  className={`border-2 border-dashed rounded-xl p-4 transition-all duration-300 ${
                    emptyFields.includes('campaignImage')
                      ? 'border-red-500 bg-red-50 animate-pulse'
                      : isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) handleImageUpload(e.dataTransfer.files[0], 'campaignImage'); }}
                >
                  <div className="flex flex-col items-center gap-3">
                    {formData.campaignImage && formData.campaignImage.trim() !== '' ? (
                      <div className="relative w-full max-w-lg mb-2">
                        <img src={formData.campaignImage} alt="" className="w-full h-auto object-cover rounded-lg" style={{ aspectRatio: '14/9' }} />
                        <button type="button" onClick={() => handleRemoveImage('campaignImage')} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600" title="Görseli kaldır">
                          <X className="text-xl" />
                        </button>
                      </div>
                    ) : (
                      <Lottie animationData={uploadAnimation} loop style={{ width: '200px', height: '200px' }} />
                    )}
                    <div className="text-center space-y-3">
                      <p className="text-sm text-gray-600 mb-2">Görseli buraya sürükleyin veya</p>
                      <label htmlFor="template-campaign-image" className="cursor-pointer">
                        <div className="inline-flex items-center gap-2 px-6 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
                          <Upload className="text-base" />
                          <span>Dosya Seçin</span>
                        </div>
                        <input
                          type="file"
                          id="template-campaign-image"
                          className="hidden"
                          accept="image/png,image/webp"
                          onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'campaignImage')}
                        />
                      </label>
                      <p className="text-xs text-gray-500">Önerilen: 1120 x 720 px (14:9) · PNG/WEBP · max. 500 KB</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Kiosk Görseli */}
              {(() => {
                const imageWidth = isGeneral ? 260 : 320;
                const imageHeight = isGeneral ? 230 : 441;
                const aspectRatio = `${imageWidth}/${imageHeight}`;
                const recommendedText = `Önerilen: ${imageWidth} x ${imageHeight} px · PNG/WEBP · max. 500 KB`;
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Kiosk Küçük Banner Görseli <span className="text-gray-500 font-normal">(Opsiyonel)</span></label>
                    <div
                      className={`border-2 border-dashed rounded-xl p-4 transition-all duration-300 ${
                        emptyFields.includes('campaignImage2')
                          ? 'border-red-500 bg-red-50 animate-pulse'
                          : isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) handleImageUpload(e.dataTransfer.files[0], 'campaignImage2'); }}
                    >
                      <div className="flex flex-col items-center gap-3">
                        {formData.campaignImage2 && formData.campaignImage2.trim() !== '' ? (
                          <div className="relative w-full max-w-lg mb-2">
                            <img src={formData.campaignImage2} alt="Kiosk" className="w-full h-auto object-cover rounded-lg" style={{ aspectRatio }} />
                            <button type="button" onClick={() => handleRemoveImage('campaignImage2')} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600" title="Görseli kaldır">
                              <X className="text-xl" />
                            </button>
                          </div>
                        ) : (
                          <Lottie animationData={uploadAnimation} loop style={{ width: '200px', height: '200px' }} />
                        )}
                        <div className="text-center space-y-3">
                          <p className="text-sm text-gray-600 mb-2">Görseli buraya sürükleyin veya</p>
                          <label htmlFor="template-campaign-image2" className="cursor-pointer">
                            <div className="inline-flex items-center gap-2 px-6 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
                              <Upload className="text-base" />
                              <span>Dosya Seçin</span>
                            </div>
                            <input
                              type="file"
                              id="template-campaign-image2"
                              className="hidden"
                              accept="image/png,image/webp"
                              onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'campaignImage2')}
                            />
                          </label>
                          <p className="text-xs text-gray-500">{recommendedText}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Genel için 3. görsel */}
              {isGeneral && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kiosk Büyük Banner (Genel) <span className="text-gray-500 font-normal">(Opsiyonel)</span></label>
                  <div
                    className={`border-2 border-dashed rounded-xl p-4 transition-all duration-300 ${
                      emptyFields.includes('campaignImage3')
                        ? 'border-red-500 bg-red-50 animate-pulse'
                        : isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) handleImageUpload(e.dataTransfer.files[0], 'campaignImage3'); }}
                  >
                    <div className="flex flex-col items-center gap-3">
                      {formData.campaignImage3 && formData.campaignImage3.trim() !== '' ? (
                        <div className="relative w-full max-w-lg mb-2">
                          <img src={formData.campaignImage3} alt="Kiosk Büyük" className="w-full h-auto object-cover rounded-lg" style={{ aspectRatio: '1000/392' }} />
                          <button type="button" onClick={() => handleRemoveImage('campaignImage3')} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600" title="Görseli kaldır">
                            <X className="text-xl" />
                          </button>
                        </div>
                      ) : (
                        <Lottie animationData={uploadAnimation} loop style={{ width: '200px', height: '200px' }} />
                      )}
                      <div className="text-center space-y-3">
                        <p className="text-sm text-gray-600 mb-2">Görseli buraya sürükleyin veya</p>
                        <label htmlFor="template-campaign-image3" className="cursor-pointer">
                          <div className="inline-flex items-center gap-2 px-6 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
                            <Upload className="text-base" />
                            <span>Dosya Seçin</span>
                          </div>
                          <input
                            type="file"
                            id="template-campaign-image3"
                            className="hidden"
                            accept="image/png,image/webp"
                            onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'campaignImage3')}
                          />
                        </label>
                        <p className="text-xs text-gray-500">Önerilen: 1000 x 392 px · PNG/WEBP · max. 500 KB</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={
                    actionLoading ||
                    !isFormValid() ||
                    (editingTemplate && !hasChanges())
                  }
                  className="flex-1 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {actionLoading ? 'Kaydediliyor...' : (editingTemplate ? 'Güncelle' : 'Oluştur')}
                </button>
                <button type="button" onClick={() => { setModalOpen(false); resetForm(); }} className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 font-medium">
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-xl w-full">
            <h3 className="text-base font-bold text-gray-900 mb-2">Taslağı Sil</h3>
            <p className="text-gray-600 mb-4">
              &quot;{deleteConfirm.campaignTitle || deleteConfirm.templateName || 'Bu taslak'}&quot; silinecek. Devam etmek istiyor musunuz?
            </p>
            <div className="flex gap-3">
              <button onClick={handleDelete} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600">
                Sil
              </button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50">
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CampaignTemplates;
